import { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from './supabase'
import { canAccessPage as _canAccessPage, canWritePage as _canWritePage } from './pageAccess'

/**
 * Shape of a `profiles` table row. Re-exports the Supabase-generated row type
 * so the shape tracks the DB automatically — any schema migration that
 * regenerates `types/supabase.ts` propagates here without a drift window.
 * Consumers must null-check nullable fields (`display_name`, `phone`, etc.).
 * @typedef {import('../../types/supabase').Database['public']['Tables']['profiles']['Row']} ProfileRow
 */

/**
 * @typedef {Object} AuthContextValue
 * @property {import('@supabase/supabase-js').Session | null} session
 * @property {ProfileRow | null} profile
 * @property {boolean} profileError
 * @property {boolean} loading
 * @property {import('@supabase/supabase-js').User | undefined} user
 * @property {boolean} isOwner
 * @property {boolean} isOperator
 * @property {boolean} isContractor
 * @property {boolean} isCrewMember
 * @property {boolean} isClient
 * @property {boolean} isStaff
 * @property {(email: string, password: string) => Promise<import('@supabase/supabase-js').AuthResponse['data']>} signIn
 * @property {(email: string, password: string, metadata?: Record<string, unknown>) => Promise<import('@supabase/supabase-js').AuthResponse['data']>} signUp
 * @property {(email: string, password: string) => Promise<void>} contractorSignUp
 * @property {(email: string, password: string) => Promise<void>} crewSignUp
 * @property {() => Promise<void>} signOut
 * @property {(updates: Record<string, unknown>) => Promise<ProfileRow | undefined>} updateProfile
 * @property {() => Promise<void>} reloadProfile
 * @property {(pageKey: string) => boolean} canAccessPage
 * @property {(pageKey: string) => boolean} canWritePage
 */

/** @type {React.Context<AuthContextValue>} */
const AuthContext = createContext(/** @type {AuthContextValue} */ ({
  session: null, profile: null, profileError: false, loading: true,
  user: undefined, isOwner: false, isOperator: false, isContractor: false,
  isCrewMember: false, isClient: false, isStaff: false,
  signIn: async () => (/** @type {any} */ ({})),
  signUp: async () => (/** @type {any} */ ({})),
  contractorSignUp: async () => {},
  crewSignUp: async () => {},
  signOut: async () => {},
  updateProfile: async () => undefined,
  reloadProfile: async () => {},
  canAccessPage: () => false,
  canWritePage: () => false,
}))

/**
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(/** @type {import('@supabase/supabase-js').Session | null} */ (null))
  const [profile, setProfile] = useState(/** @type {ProfileRow | null} */ (null))
  const [loading, setLoading] = useState(true)
  const [profileError, setProfileError] = useState(false)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session?.user) loadProfile(session.user.id)
      else setLoading(false)
    })

    // Listen for auth changes.
    // Only clear state on explicit SIGNED_OUT — not on transient refresh failures
    // that could log crew members out mid-shift on spotty mobile networks.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setSession(session)
        loadProfile(session.user.id)
      } else if (event === 'SIGNED_OUT') {
        setSession(null)
        setProfile(null)
        setLoading(false)
      }
      // For other events with null session (e.g. TOKEN_REFRESHED failure),
      // keep existing state — the visibilitychange handler will retry.
    })

    return () => subscription.unsubscribe()
  }, [])

  /**
   * @param {string} userId
   */
  async function loadProfile(userId) {
    let { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    // Retry once after short delay — defense-in-depth for slow replication
    if (!data) {
      await new Promise(r => setTimeout(r, 500))
      const retry = await supabase.from('profiles').select('*').eq('id', userId).single()
      data = retry.data
      error = retry.error
    }

    if (data) {
      setProfile(data)
      setProfileError(false)
    } else {
      console.error('No profile found for user after retry — account setup may be incomplete', error)
      setProfile(null)
      setProfileError(true)
    }
    setLoading(false)
  }

  /**
   * @param {string} email
   * @param {string} password
   */
  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  /**
   * @param {string} email
   * @param {string} password
   * @param {object} [metadata] - Optional user metadata stored on auth.users (merged into raw_user_meta_data).
   * @param {'ca' | 'us'} [metadata.country] - Tenant country; read by handle_new_user trigger. Defaults to 'ca' in the trigger if absent. Only set when VITE_FEATURE_US_SUPPORT === 'true' and user explicitly picks.
   * @param {string} [metadata.company_name]
   * @param {string} [metadata.full_name]
   * @param {string} [metadata.industry_template]
   * @param {string} [metadata.role]
   */
  async function signUp(email, password, metadata = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata }
    })
    if (error) throw error
    return data
  }

  /**
   * @param {string} email
   * @param {string} password
   */
  async function contractorSignUp(email, password) {
    const res = await fetch('/api/contractor-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const json = await res.json()
    if (!res.ok) throw new Error(json.error || 'Signup failed')
    // Auto-sign in after successful registration
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  async function signOut() {
    await supabase.auth.signOut()
    setSession(null)
    setProfile(null)
  }

  /**
   * @param {Record<string, unknown>} updates
   * @returns {Promise<ProfileRow | undefined>}
   */
  async function updateProfile(updates) {
    if (!session?.user?.id) throw new Error('Not logged in')
    const allowedFields = ['display_name', 'send_from_email', 'phone', 'avatar_url']
    const filtered = Object.fromEntries(
      Object.entries(updates).filter(([key]) => allowedFields.includes(key))
    )
    if (Object.keys(filtered).length === 0) return
    const { data, error } = await supabase
      .from('profiles')
      .update(filtered)
      .eq('id', session.user.id)
      .select()
      .single()
    if (error) throw error
    setProfile(data)
    return data
  }

  async function reloadProfile() {
    if (session?.user) await loadProfile(session.user.id)
  }

  const isOwner = profile?.role === 'owner'
  const isOperator = profile?.role === 'operator'
  const isContractor = profile?.role === 'subcontractor'
  const isCrewMember = isContractor
  const isClient = profile?.role === 'client'
  const isStaff = isOwner || isOperator
  const crewSignUp = contractorSignUp
  /** @param {string} pageKey @returns {boolean} */
  const canAccessPage = (pageKey) => _canAccessPage(pageKey, profile?.role, profile?.capabilities)
  /** @param {string} pageKey @returns {boolean} */
  const canWritePage = (pageKey) => _canWritePage(pageKey, profile?.role, profile?.capabilities)

  return (
    <AuthContext.Provider value={/** @type {AuthContextValue} */ ({
      session,
      profile,
      profileError,
      loading,
      signIn,
      signUp,
      contractorSignUp,
      crewSignUp,
      signOut,
      updateProfile,
      isOwner,
      isOperator,
      isContractor,
      isCrewMember,
      isClient,
      isStaff,
      reloadProfile,
      canAccessPage,
      canWritePage,
      user: session?.user,
    })}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * @returns {AuthContextValue}
 */
export function useAuth() {
  return useContext(AuthContext)
}
